import React, { Suspense, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jem-open/jem-ui";
import { Clock, RefreshCw } from "lucide-react";
import { useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { SyncRun, SyncRunStatus } from "../services/model/SyncRun";
import { useGetSyncRunsApiService } from "../services/api/GetSyncRunsApiService";
import { useGetSyncRunsSummaryApiService } from "../services/api/GetSyncRunsSummaryApiService";
import { getPeriodDateRange, Period, PickerDateRange } from "../services/helpers";
import SyncRunsTableComponent from "./SyncRunsTableComponent";
import SyncRunDetailDialog from "./SyncRunDetailDialog";
import PeriodSelector from "./PeriodSelector";
import StatusFilter from "./StatusFilter";
import SyncRunsSummary from "./SyncRunsSummary";

const EmployeeBlockEventsTable = React.lazy(() => import("./EmployeeBlockEventsTable"));

type DataState = "ready" | "empty" | "error";

const PAGE_SIZE = 10;

const VALID_PERIODS: Period[] = ["today", "week", "month", "custom"];

const IntegrationsHealthTable: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runInView, setRunInView] = useState<SyncRun | null>(null);

  const viewMode = useMemo<"by-sync" | "by-employee">(() => {
    const v = searchParams.get("viewMode");
    return v === "by-employee" ? "by-employee" : "by-sync";
  }, [searchParams]);

  // Derive filter state from URL — handles back/forward automatically
  const period = useMemo<Period>(() => {
    const p = searchParams.get("period");
    return VALID_PERIODS.includes(p as Period) ? (p as Period) : "today";
  }, [searchParams]);

  const customDateRange = useMemo<PickerDateRange | undefined>(() => {
    const start = searchParams.get("range_start");
    if (!start) return undefined;
    const end = searchParams.get("range_end");
    return { from: new Date(start), to: end ? new Date(end) : undefined };
  }, [searchParams]);

  const statusFilter = useMemo<SyncRunStatus | "all">(() => {
    const s = searchParams.get("status");
    const valid = Object.values(SyncRunStatus) as string[];
    return s === "all" || valid.includes(s ?? "") ? (s as SyncRunStatus | "all") : "all";
  }, [searchParams]);

  const sortCol = useMemo(() => searchParams.get("sort_col") ?? "start", [searchParams]);
  const sortDir = useMemo<"asc" | "desc">(() => {
    const d = searchParams.get("sort_dir");
    return d === "asc" ? "asc" : "desc";
  }, [searchParams]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get("page") ?? "1", 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  }, [searchParams]);

  const periodDateRange = useMemo(
    () => getPeriodDateRange(period, customDateRange),
    [period, customDateRange]
  );

  const { data, isLoading, isFetching, isError } = useGetSyncRunsApiService({
    start_date: periodDateRange.start_date,
    end_date: periodDateRange.end_date,
    page,
    page_size: PAGE_SIZE,
    sort_by: `${sortDir === "desc" ? "-" : ""}${sortCol}`,
    status: statusFilter !== "all" ? statusFilter : undefined,
  });

  const { data: summary } = useGetSyncRunsSummaryApiService({
    start_date: periodDateRange.start_date,
    end_date: periodDateRange.end_date,
    status: statusFilter !== "all" ? statusFilter : undefined,
  });

  const runs = data?.results ?? [];
  const totalPages = data?.pages ?? 1;
  const totalCount = data?.count ?? 0;

  // Clamp out-of-range deep-linked page values once data is available
  useEffect(() => {
    if (!data) return;
    const maxPage = data.pages > 0 ? data.pages : 1;
    if (page > maxPage) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (maxPage === 1) next.delete("page"); else next.set("page", String(maxPage));
          return next;
        },
        { replace: true }
      );
    }
  }, [page, data, setSearchParams]);

  const isTableLoading = isLoading || isFetching;

  const getDataState = (): DataState => {
    if (isError) return "error";
    if (runs.length === 0) return "empty";
    return "ready";
  };
  const dataState = getDataState();

  const totalCreated = summary?.total_creations;
  const totalUpdated = summary?.total_updates;
  const totalFailedCreated = summary?.total_failed_creations;
  const totalFailedUpdated = summary?.total_failed_updates;

  const lastSyncedRelative = useMemo(() => {
    // Only meaningful on the canonical view: page 1, newest-first by start date.
    // On other pages or sort orders the visible runs are not the most recent.
    if (page !== 1 || sortCol !== "start" || sortDir !== "desc") return null;
    const latestEnd = runs
      .map((r) => r.end)
      .filter((e): e is string => e !== null)
      .sort()
      .pop();
    if (!latestEnd) return null;
    return moment(latestEnd).fromNow();
  }, [runs, page, sortCol, sortDir]);

  const resetPage = (next: URLSearchParams): void => {
    next.delete("page");
  };

  const handlePresetPeriod = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "today") next.delete("period"); else next.set("period", value);
        next.delete("range_start");
        next.delete("range_end");
        resetPage(next);
        return next;
      },
      { replace: true }
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRangeSelect = (range: any): void => {
    if (!range?.from) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("period", "custom");
        next.set("range_start", moment(range.from).format("YYYY-MM-DD"));
        if (range.to) next.set("range_end", moment(range.to).format("YYYY-MM-DD"));
        else next.delete("range_end");
        resetPage(next);
        return next;
      },
      { replace: true }
    );
  };

  const handleStatusChange = (value: SyncRunStatus | "all"): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") next.delete("status"); else next.set("status", value);
        resetPage(next);
        return next;
      },
      { replace: true }
    );
  };

  const handleSortChange = (col: string, dir: "asc" | "desc"): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (col === "start" && dir === "desc") {
          next.delete("sort_col");
          next.delete("sort_dir");
        } else {
          next.set("sort_col", col);
          next.set("sort_dir", dir);
        }
        resetPage(next);
        return next;
      },
      { replace: true }
    );
  };

  const handlePageChange = (newPage: number): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newPage === 1) next.delete("page"); else next.set("page", String(newPage));
        return next;
      },
      { replace: true }
    );
  };

  const handleViewModeChange = (v: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === "by-sync") next.delete("viewMode"); else next.set("viewMode", v);
        return next;
      },
      { replace: true }
    );
  };

  const handleRefresh = (): void => {
    queryClient.invalidateQueries(["GetSyncRunsApiService"]);
    queryClient.invalidateQueries(["GetSyncRunsSummaryApiService"]);
    queryClient.invalidateQueries(["GetSyncRunRecordsApiService"]);
  };


  return (
    <div className="pb-xl pt-md">
      <SyncRunsSummary
        totalCreated={totalCreated}
        totalUpdated={totalUpdated}
        totalFailedCreated={totalFailedCreated}
        totalFailedUpdated={totalFailedUpdated}
      />

      <Tabs value={viewMode} onValueChange={handleViewModeChange}>
        <TabsList className="mb-xs">
          <TabsTrigger value="by-sync" className="font-semibold">By sync</TabsTrigger>
          <TabsTrigger value="by-employee" className="font-semibold">By employee</TabsTrigger>
        </TabsList>

        <p className="mb-md text-sm text-greyscale-text-caption">
          {viewMode === "by-sync"
            ? "A log of every sync that ran during the selected period. Use the filters to find what you're looking for."
            : "The employee tab only shows employees whose access status changed during the selected period (newly blocked or unblocked)."}
        </p>

        {/* Control row */}
        <div className="flex flex-wrap items-end gap-md justify-between">
          <div className="flex flex-wrap items-end gap-sm">
            <PeriodSelector
              period={period}
              customDateRange={customDateRange}
              onPresetChange={handlePresetPeriod}
              onRangeSelect={handleRangeSelect}
            />
            {viewMode === "by-sync" && (
              <StatusFilter value={statusFilter} onChange={handleStatusChange} />
            )}
          </div>
          <div className="flex items-center gap-sm">
            {lastSyncedRelative && viewMode === "by-sync" && (
              <span className="flex items-center gap-xxs text-xs text-greyscale-text-caption">
                <Clock size={14} />
                Last synced {lastSyncedRelative}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw size={14} />
              Refresh
            </Button>
          </div>
        </div>

        {/* By sync tab */}
        <TabsContent value="by-sync" className="mt-0">
          {dataState === "error" && !isTableLoading && (
            <Alert variant="destructive" className="mt-lg">
              <AlertTitle>We couldn&apos;t load integration health</AlertTitle>
              <AlertDescription>
                The metrics service is unreachable. Try refreshing in a minute.
                <div className="mt-0">
                  <Button variant="outline" size="sm" onClick={handleRefresh}>
                    <RefreshCw size={14} />
                    Try again
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {dataState !== "error" && (
            <SyncRunsTableComponent
              runs={runs}
              isLoading={isTableLoading}
              sortCol={sortCol}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              onRunClick={setRunInView}
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={handlePageChange}
            />
          )}
        </TabsContent>

        {/* By employee tab */}
        <TabsContent value="by-employee" className="mt-0">
          <Suspense fallback={null}>
            <EmployeeBlockEventsTable
              startDate={periodDateRange.start_date}
              endDate={periodDateRange.end_date}
            />
          </Suspense>
        </TabsContent>
      </Tabs>

      <SyncRunDetailDialog
        run={runInView}
        onClose={() => setRunInView(null)}
      />
    </div>
  );
};

export default IntegrationsHealthTable;
